import React, { useState, useEffect } from 'react';
import { procurementApi } from '../lib/api';
import { formatDate, formatCurrency, PROCUREMENT_SECTORS, PROCUREMENT_STATUSES } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Plus, MagnifyingGlass, Pencil, Trash, Calendar, CurrencyDollar,
  Buildings, Lightning, Factory, Truck, Broadcast, Wrench
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const SECTOR_ICONS = {
  infrastructure: Buildings,
  energy: Lightning,
  telecommunication: Broadcast,
  logistic: Truck,
  transports: Truck,
  services: Wrench,
};

export default function ProcurementPage() {
  const [procurements, setProcurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSector, setActiveSector] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProcurement, setEditingProcurement] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    sector: 'infrastructure',
    procurement_type: '',
    status: 'draft',
    budget: 0,
    start_date: '',
    end_date: '',
    description: '',
    requirements: [],
    vendors: []
  });
  const [requirementInput, setRequirementInput] = useState('');
  const [vendorInput, setVendorInput] = useState('');

  useEffect(() => {
    fetchProcurements();
  }, [activeSector, statusFilter]);

  const fetchProcurements = async () => {
    try {
      const params = { limit: 100 };
      if (activeSector !== 'all') params.sector = activeSector;
      if (statusFilter) params.status = statusFilter;
      
      const response = await procurementApi.getAll(params);
      setProcurements(response.data);
    } catch (error) {
      toast.error('Failed to fetch procurements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProcurement) {
        await procurementApi.update(editingProcurement.id, formData);
        toast.success('Procurement updated');
      } else {
        await procurementApi.create(formData);
        toast.success('Procurement created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchProcurements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save procurement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this procurement?')) return;
    try {
      await procurementApi.delete(id);
      toast.success('Procurement deleted');
      fetchProcurements();
    } catch (error) {
      toast.error('Failed to delete procurement');
    }
  };

  const openEditDialog = (procurement) => {
    setEditingProcurement(procurement);
    setFormData({
      title: procurement.title,
      sector: procurement.sector,
      procurement_type: procurement.procurement_type || '',
      status: procurement.status,
      budget: procurement.budget || 0,
      start_date: procurement.start_date || '',
      end_date: procurement.end_date || '',
      description: procurement.description || '',
      requirements: procurement.requirements || [],
      vendors: procurement.vendors || []
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProcurement(null);
    setFormData({
      title: '',
      sector: 'infrastructure',
      procurement_type: '',
      status: 'draft',
      budget: 0,
      start_date: '',
      end_date: '',
      description: '',
      requirements: [],
      vendors: []
    });
    setRequirementInput('');
    setVendorInput('');
  };

  const addRequirement = () => {
    if (requirementInput.trim()) {
      setFormData({ ...formData, requirements: [...formData.requirements, requirementInput.trim()] });
      setRequirementInput('');
    }
  };

  const removeRequirement = (index) => {
    setFormData({ 
      ...formData, 
      requirements: formData.requirements.filter((_, i) => i !== index) 
    });
  };

  const addVendor = () => {
    if (vendorInput.trim()) {
      setFormData({ ...formData, vendors: [...formData.vendors, vendorInput.trim()] });
      setVendorInput('');
    }
  };

  const removeVendor = (index) => {
    setFormData({ 
      ...formData, 
      vendors: formData.vendors.filter((_, i) => i !== index) 
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = PROCUREMENT_STATUSES.find(s => s.id === status);
    return (
      <Badge className={`${statusConfig?.color || 'bg-zinc-500'} text-white`}>
        {statusConfig?.label || status}
      </Badge>
    );
  };

  const filteredProcurements = procurements.filter(p => 
    !search || p.title.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="procurement-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Procurement</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage procurement across sectors</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsDialogOpen(true); }} 
          className="rounded-sm"
          data-testid="add-procurement-btn"
        >
          <Plus size={18} className="mr-2" />
          New Procurement
        </Button>
      </div>

      {/* Sector Tabs */}
      <Tabs value={activeSector} onValueChange={setActiveSector}>
        <TabsList className="bg-muted/50">
          <TabsTrigger value="all" data-testid="tab-all">All Sectors</TabsTrigger>
          {PROCUREMENT_SECTORS.map((sector) => {
            const Icon = SECTOR_ICONS[sector.id] || Factory;
            return (
              <TabsTrigger key={sector.id} value={sector.id} data-testid={`tab-${sector.id}`}>
                <Icon size={16} className="mr-1" />
                {sector.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            placeholder="Search procurements..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="search-procurement"
          />
        </div>
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {PROCUREMENT_STATUSES.map((status) => (
              <SelectItem key={status.id} value={status.id}>{status.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Procurement List */}
      <div className="grid gap-4">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Loading...</div>
        ) : filteredProcurements.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">No procurements found</div>
        ) : (
          filteredProcurements.map((procurement) => {
            const Icon = SECTOR_ICONS[procurement.sector] || Factory;
            return (
              <div
                key={procurement.id}
                className="bg-card border border-border p-4 rounded-sm hover:-translate-y-[1px] transition-transform"
                data-testid={`procurement-card-${procurement.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-sm bg-primary/10 flex items-center justify-center">
                      <Icon size={20} className="text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{procurement.title}</h3>
                        {getStatusBadge(procurement.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {PROCUREMENT_SECTORS.find(s => s.id === procurement.sector)?.label} • 
                        {procurement.procurement_type || 'General'}
                      </p>
                      {procurement.description && (
                        <p className="text-sm mt-2 text-muted-foreground line-clamp-2">
                          {procurement.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-sm">
                        <div className="flex items-center gap-1">
                          <CurrencyDollar size={14} className="text-muted-foreground" />
                          <span className="font-semibold">{formatCurrency(procurement.budget)}</span>
                        </div>
                        {procurement.start_date && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Calendar size={14} />
                            <span>{formatDate(procurement.start_date)} - {formatDate(procurement.end_date)}</span>
                          </div>
                        )}
                      </div>
                      {procurement.vendors?.length > 0 && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-muted-foreground">Vendors:</span>
                          {procurement.vendors.slice(0, 3).map((vendor) => (
                            <Badge key={`v-${vendor}`} variant="outline" className="text-xs">{vendor}</Badge>
                          ))}
                          {procurement.vendors.length > 3 && (
                            <span className="text-xs text-muted-foreground">+{procurement.vendors.length - 3} more</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(procurement)}
                      data-testid={`edit-procurement-${procurement.id}`}
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(procurement.id)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-procurement-${procurement.id}`}
                    >
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProcurement ? 'Edit Procurement' : 'New Procurement'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                data-testid="procurement-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sector *</Label>
                <Select
                  value={formData.sector}
                  onValueChange={(value) => setFormData({ ...formData, sector: value })}
                >
                  <SelectTrigger data-testid="procurement-sector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROCUREMENT_SECTORS.map((sector) => (
                      <SelectItem key={sector.id} value={sector.id}>{sector.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="procurement-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROCUREMENT_STATUSES.map((status) => (
                      <SelectItem key={status.id} value={status.id}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Procurement Type</Label>
                <Input
                  value={formData.procurement_type}
                  onChange={(e) => setFormData({ ...formData, procurement_type: e.target.value })}
                  placeholder="e.g., RFP, Tender, Quote"
                  data-testid="procurement-type"
                />
              </div>
              <div className="space-y-2">
                <Label>Budget</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: parseFloat(e.target.value) || 0 })}
                  data-testid="procurement-budget"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  data-testid="procurement-start"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  data-testid="procurement-end"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="procurement-description"
              />
            </div>
            <div className="space-y-2">
              <Label>Requirements</Label>
              <div className="flex gap-2">
                <Input
                  value={requirementInput}
                  onChange={(e) => setRequirementInput(e.target.value)}
                  placeholder="Add requirement"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
                />
                <Button type="button" variant="outline" onClick={addRequirement}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.requirements.map((req, i) => (
                  <Badge key={`req-${req}-${i}`} variant="secondary" className="cursor-pointer" onClick={() => removeRequirement(i)}>
                    {req} ×
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Vendors</Label>
              <div className="flex gap-2">
                <Input
                  value={vendorInput}
                  onChange={(e) => setVendorInput(e.target.value)}
                  placeholder="Add vendor"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addVendor())}
                />
                <Button type="button" variant="outline" onClick={addVendor}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.vendors.map((vendor, i) => (
                  <Badge key={`vd-${vendor}-${i}`} variant="secondary" className="cursor-pointer" onClick={() => removeVendor(i)}>
                    {vendor} ×
                  </Badge>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="procurement-submit">
                {editingProcurement ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
